import { memo, useEffect, useState } from 'react';

import { Controller, useForm } from 'react-hook-form';
import { useDispatch } from 'react-redux';

import { yupResolver } from '@hookform/resolvers/yup';
import EditIcon from 'assets/edit.svg';
import Button from 'common-ui/button';
import Input from 'common-ui/input';
import Modal from 'common-ui/modal';
import Switch from 'common-ui/switch';
import TextArea from 'common-ui/textArea';
import TextEditor from 'common-ui/textEditor';
import {
  createSalesScript,
  editSalesScript,
  getSalesScripts,
} from 'features/sales/salesActions';
import { TextEditorWrapper } from 'pages/projectManagement/createEditTicket/CreateEditTicket.styles';
import * as Yup from 'yup';

import { BtnWrapper, Form, ShiftControl } from '../Sales.styles';
import useSearchData from './useSearchData';

const nameRegex = /^[A-Za-zԱ-Ֆա-ֆև0-9 ,.:#\-/'&()]+$/;

const schema = Yup.object().shape({
  name: Yup.string()
    .required('Name is required')
    .matches(
      nameRegex,
      'Only  Latin and Armenian letters, numbers, and allowed special characters'
    ),
  script: Yup.string()
    .required('Script is required')
    .max(10000, 'Script must be at most 10000 characters'),
  isEnabled: Yup.boolean(),
});

const Create = ({ isEdit = false, initialData = null, onCloseEdit }) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalWidth, setModalWidth] = useState('430px');
  const { searchData } = useSearchData();

  const dispatch = useDispatch();

  const {
    handleSubmit,
    control,
    formState: { errors },
    reset,
  } = useForm({
    resolver: yupResolver(schema),
    defaultValues: {
      name: '',
      description: '',
      script: '',
      isEnabled: true,
    },
    shouldFocusError: false,
  });
  useEffect(() => {
    if (initialData) {
      reset({
        name: initialData.name || '',
        description: initialData.description || '',
        script: initialData.script || '',
        isEnabled: initialData.isEnabled ?? true,
      });
    }
  }, [initialData, reset]);

  const handleOpenModal = () => setIsModalOpen(true);

  const handleCloseModal = () => {
    setIsModalOpen(false);
    reset();
    if (onCloseEdit) onCloseEdit();
  };

  const onSubmit = (data) => {
    const body = {
      name: data.name,
      description: data.description,
      script: data.script,
      isEnabled: data.isEnabled,
    };

    const action =
      isEdit && initialData?.uuid
        ? editSalesScript({ uuid: initialData.uuid, body })
        : createSalesScript(body);

    dispatch(action).then(() => {
      dispatch(getSalesScripts(searchData));
      handleCloseModal();
    });
  };

  const getPlainTextLength = (html = '') => html.replace(/<[^>]*>/g, '').length;

  const handleScriptChange = (content, onChange) => {
    const textOnly = content.replace(/<[^>]*>/g, '');
    if (textOnly.length <= 100) {
      onChange(content);
    }
  };

  const handleScriptBeforeInput = (e, value) => {
    const textOnly = getPlainTextLength(value);
    if (textOnly >= 100) {
      e.preventDefault();
    }
  };

  const handleScriptKeyDown = (e, value) => {
    const allowedKeys = [
      'Backspace',
      'Delete',
      'ArrowLeft',
      'ArrowRight',
      'ArrowUp',
      'ArrowDown',
    ];
    const textOnly = getPlainTextLength(value);
    if (textOnly >= 100 && !allowedKeys.includes(e.key)) {
      e.preventDefault();
    }
  };

  const handleScriptPaste = (e, value) => {
    const pastedText = e.clipboardData.getData('text/plain');
    const textOnly = getPlainTextLength(value);
    if (textOnly + pastedText.length > 100) {
      e.preventDefault();
    }
  };

  useEffect(() => {
    const handleResize = () => {
      setModalWidth(window.innerWidth < 500 ? '370px' : '470px');
    };

    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  return (
    <>
      {isEdit ? (
        <ShiftControl onClick={handleOpenModal}>
          <img alt="Edit icon" src={EditIcon} />
        </ShiftControl>
      ) : (
        <BtnWrapper>
          <Button secondary onClick={handleOpenModal} className="h-38">
            + Add Sales Script
          </Button>
        </BtnWrapper>
      )}

      <Modal
        isOpen={isModalOpen}
        onClose={handleCloseModal}
        width={modalWidth}
        onOk={handleSubmit(onSubmit)}
        title={isEdit ? 'Edit Sales Script' : 'Create Sales Script'}
        footer={true}
      >
        <Form onSubmit={handleSubmit(onSubmit)}>
          <Controller
            name="name"
            control={control}
            render={({ field }) => (
              <Input
                {...field}
                label="Name"
                error={errors.name?.message}
                placeholder="Enter name"
                maxLength={50}
                required
              />
            )}
          />

          <Controller
            name="description"
            control={control}
            render={({ field }) => (
              <TextArea
                {...field}
                label="Description"
                resizeHorizontal={false}
                error={errors.description?.message}
                placeholder="Enter description"
                maxLength={250}
              />
            )}
          />
          <Controller
            name="script"
            control={control}
            render={({ field }) => (
              <TextEditorWrapper>
                <TextEditor
                  {...field}
                  label="Add script"
                  errorText={errors.script?.message}
                  placeholder="Enter script"
                  maxLength={10000}
                  required
                />
              </TextEditorWrapper>
            )}
          />

          <Controller
            name="isEnabled"
            control={control}
            render={({ field }) => (
              <div style={{ marginTop: '16px' }}>
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '10px',
                    justifyContent: 'end',
                  }}
                >
                  <label style={{ fontSize: '14px', fontWeight: 500 }}>
                    {field.value ? 'Enabled' : 'Disabled'}
                  </label>
                  <Switch isOn={field.value} onToggle={() => field.onChange(!field.value)} />
                </div>
              </div>
            )}
          />
        </Form>
      </Modal>
    </>
  );
};

export default memo(Create);
